import React from "react";
import styled from "styled-components";
import { Close } from "@material-ui/icons";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import PosFieldLabel from "../../../components/shared/PosFieldLabel";
import { BLUEPRINT_STEP_CODES } from "../../../constants/workflowStepCodes";
import { getOrderProductDisplayName } from "../../../utils/orderProductDisplay";

const OverlayContainer = styled.div`
    overflow: hidden;
`;

const Overlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    color: white;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    top: 0;
    background-color: rgba(white, 0.5);
    visibility: visible;
`;

const ModalContainer = styled.div`
    height: 100%;
    width: 100%;
    opacity: 0.75;
    background: black;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    z-index: 10;
`;

const ModalDetails = styled.div`
    background-color: rgba(white, 0.5);
    color: black;
    height: 100%;
    width: 50%;
    display: flex;
    z-index: 102;
    
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const EmptyModalDetails = styled.div`
    background-color: rgba(white, 0.5);
    color: black;
    height: 100%;
    width: 50%;
    display: flex;
    z-index: 102;
    
    @media (max-width: 768px) {
        display: none;
    }
`;

const ModalItemContainer = styled.div`
    background-color: white;
    color: black;
    height: 100%;
    width: 100%;
    display: grid;
`;

const BlueprintUploadModal = ({
    addBlueprintModalState,
    resetAddBlueprintModal,
    isUploadingBlueprint,
    handleBlueprintUpload,
    setAddBlueprintModalState,
    orderProducts,
}) => {
    if (!addBlueprintModalState) return null;

    return (
        <OverlayContainer className="blueprint-upload-modal-root product-picker-modal-root">
            <ModalContainer className="product-picker-backdrop" onClick={resetAddBlueprintModal} />
            <Overlay className="product-picker-overlay">
                <EmptyModalDetails
                    onClick={resetAddBlueprintModal}
                ></EmptyModalDetails>
                <ModalDetails className="product-picker-panel">
                    <ModalItemContainer className="product-picker-panel-inner">
                        {/* Mobile-only header: same look as Select Materials modal for seamless design */}
                        <div className="purchase-order-select-materials-modal-header product-picker-mobile-header">
                            <h3>
                                <span className="pos-section-header-with-tag">
                                    <StepCodeTag code={BLUEPRINT_STEP_CODES.uploadBlueprint} />
                                    Upload Blueprint
                                </span>
                            </h3>
                            <button
                                type="button"
                                className="purchase-order-select-materials-modal-close"
                                onClick={resetAddBlueprintModal}
                                aria-label="Close"
                            >
                                <Close />
                            </button>
                        </div>
                        <div className="product-picker-modal-body blueprint-upload-modal-body">
                        {isUploadingBlueprint ? (
                            <div className="container h-full mx-auto flex flex-col justify-center items-center p-4 h-64">
                                <div className="animate-spin w-16 h-32 border-4 border-blue-400 border-t-transparent rounded-full mb-4"></div>
                                <p className="text-lg font-medium text-gray-700">
                                    Uploading blueprints...
                                </p>
                                <p className="text-sm text-gray-500">
                                    Please wait while your files are being
                                    processed
                                </p>
                            </div>
                        ) : (
                            <div className="p-6 bg-white h-full overflow-y-auto blueprint-upload-form">
                                <div className="mb-4 text-2xl font-bold blueprint-upload-inner-title flex items-center gap-2 flex-wrap">
                                    <StepCodeTag code="B1" />
                                    Add Blueprint
                                </div>

                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();

                                        // Prevent submission if already uploading
                                        if (isUploadingBlueprint) {
                                            return;
                                        }

                                        const formData = new FormData(e.target);
                                        const files = e.target.file.files;
                                        const name = formData.get("name");
                                        const productIndex = formData.get("product");

                                        // Get itemName from selected product
                                        let itemName = "";
                                        if (productIndex && orderProducts && orderProducts.length > 0) {
                                            const selectedProduct = orderProducts[parseInt(productIndex)];
                                            if (selectedProduct) {
                                                itemName = getOrderProductDisplayName(selectedProduct);
                                            }
                                        }

                                        if (files.length > 0) {
                                            try {
                                                for (const file of files) {
                                                    // Check for Chinese characters in filename
                                                    if (
                                                        /[\u4E00-\u9FFF]/.test(
                                                            file.name
                                                        )
                                                    ) {
                                                        alert(
                                                            "Files with Chinese characters in the name are not allowed"
                                                        );
                                                        return;
                                                    }

                                                    const fileName =
                                                        files.length > 1
                                                            ? `${
                                                                  name ||
                                                                  file.name
                                                              } (${
                                                                  Array.from(
                                                                      files
                                                                  ).indexOf(
                                                                      file
                                                                  ) + 1
                                                              })`
                                                            : name || file.name;
                                                    await handleBlueprintUpload(
                                                        file,
                                                        fileName,
                                                        itemName,
                                                    );
                                                }
                                                alert(
                                                    "All blueprints uploaded successfully"
                                                );
                                                setAddBlueprintModalState(false);
                                            } catch (error) {
                                                alert(
                                                    "Failed to upload one or more blueprints"
                                                );
                                            }
                                        } else {
                                            alert("Please select file(s)");
                                        }
                                    }}
                                >
                                    <div className="mb-4">
                                        <PosFieldLabel code={BLUEPRINT_STEP_CODES.blueprintName}>
                                            Blueprint Name
                                        </PosFieldLabel>
                                        <input
                                            type="text"
                                            name="name"
                                            disabled={isUploadingBlueprint}
                                            className={`w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isUploadingBlueprint
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                            }`}
                                            placeholder="Enter blueprint name"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <PosFieldLabel code={BLUEPRINT_STEP_CODES.selectProduct} className="required">
                                            Select Product
                                        </PosFieldLabel>
                                        <select
                                            name="product"
                                            required
                                            disabled={isUploadingBlueprint}
                                            className={`w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isUploadingBlueprint
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select a product</option>
                                            {orderProducts && orderProducts.map((product, index) => (
                                                <option key={index} value={index}>
                                                    {getOrderProductDisplayName(product) || `Product ${index + 1}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <p className="text-sm text-gray-600">
                                            Maximum File Size : 5MB.
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            Click{" "}
                                            <a
                                                href="http://ilovepdf.com/compress_pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-500 hover:text-blue-700 underline"
                                            >
                                                HERE
                                            </a>{" "}
                                            to compress your document before
                                            upload.
                                        </p>
                                    </div>

                                    <div className="mb-6">
                                        <PosFieldLabel code={BLUEPRINT_STEP_CODES.selectFile}>
                                            Select File
                                        </PosFieldLabel>
                                        <div className="relative">
                                            <input
                                                type="file"
                                                name="file"
                                                required
                                                multiple
                                                disabled={isUploadingBlueprint}
                                                className={`absolute inset-0 w-full h-full opacity-0 z-10 ${
                                                    isUploadingBlueprint
                                                        ? "cursor-not-allowed"
                                                        : "cursor-pointer"
                                                }`}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg,.dxf"
                                                onChange={(e) => {
                                                    const files = Array.from(
                                                        e.target.files
                                                    );
                                                    const MAX_FILE_SIZE =
                                                        5 * 1024 * 1024; // 5MB in bytes

                                                    // Check each file size
                                                    const oversizedFiles =
                                                        files.filter(
                                                            (file) =>
                                                                file.size >
                                                                MAX_FILE_SIZE
                                                        );
                                                    if (
                                                        oversizedFiles.length > 0
                                                    ) {
                                                        alert(
                                                            `The following files exceed the 5MB limit:\n${oversizedFiles
                                                                .map(
                                                                    (f) => f.name
                                                                )
                                                                .join("\n")}`
                                                        );
                                                        e.target.value = ""; // Clear the file input
                                                        document.getElementById(
                                                            "blueprint-file-name-display"
                                                        ).textContent =
                                                            "No files selected";
                                                        return;
                                                    }

                                                    const fileNames = files
                                                        .map((file) => file.name)
                                                        .join(", ");
                                                    document.getElementById(
                                                        "blueprint-file-name-display"
                                                    ).textContent =
                                                        fileNames ||
                                                        "No files selected";
                                                }}
                                            />
                                            <div
                                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                                    isUploadingBlueprint
                                                        ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                                                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
                                                }`}
                                            >
                                                <div className="flex flex-col items-center">
                                                    <svg
                                                        className="w-12 h-12 text-gray-400 mb-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                                        />
                                                    </svg>
                                                    <p
                                                        className={`text-lg font-medium mb-2 ${
                                                            isUploadingBlueprint
                                                                ? "text-gray-400"
                                                                : "text-gray-600"
                                                        }`}
                                                    >
                                                        Drop your files here or
                                                        click to browse
                                                    </p>
                                                    <p
                                                        className={`text-sm mb-4 ${
                                                            isUploadingBlueprint
                                                                ? "text-gray-400"
                                                                : "text-gray-500"
                                                        }`}
                                                    >
                                                        Supports: PDF, DOC, DOCX,
                                                        JPG, JPEG, PNG, DWG, DXF
                                                        (Max 5MB per file)
                                                    </p>
                                                    <div
                                                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                                            isUploadingBlueprint
                                                                ? "bg-gray-300 text-gray-500"
                                                                : "bg-orange-400 text-white"
                                                        }`}
                                                    >
                                                        Choose Files
                                                    </div>
                                                    <p
                                                        id="blueprint-file-name-display"
                                                        className="text-sm text-gray-600 mt-2 font-medium"
                                                    >
                                                        No files selected
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={resetAddBlueprintModal}
                                            disabled={isUploadingBlueprint}
                                            className={`px-4 py-2 rounded-lg ${
                                                isUploadingBlueprint
                                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                    : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                                            }`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isUploadingBlueprint}
                                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                                                isUploadingBlueprint
                                                    ? "bg-gray-400 text-white cursor-not-allowed"
                                                    : "bg-blue-400 text-white hover:bg-blue-600"
                                            }`}
                                        >
                                            <StepCodeTag code={BLUEPRINT_STEP_CODES.upload} />
                                            Upload
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                        </div>
                    </ModalItemContainer>
                </ModalDetails>
            </Overlay>
        </OverlayContainer>
    );
};

export default BlueprintUploadModal;
